import { useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';

const EVERY_MS = 60 * 1000;
const IDLE_AFTER_MS = 5 * 60 * 1000;

/**
 * "Still here" for the Full Admin metrics page: about once a minute while the
 * person is actually using RiskLo (tab visible and some mouse, key, scroll or
 * touch in the last 5 minutes), with the page they're on. Nothing is sent
 * when signed out, idle or in a background tab.
 */
export default function useActivityPing(page, signedIn) {
  const pageRef = useRef(page);
  const lastInput = useRef(Date.now());
  const lastSent = useRef(0);
  pageRef.current = page;

  useEffect(() => {
    if (!signedIn) return undefined;
    const device = () => (window.matchMedia?.('(max-width: 768px)').matches ? 'mobile' : 'desktop');
    const send = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastInput.current > IDLE_AFTER_MS) return;
      if (Date.now() - lastSent.current < EVERY_MS - 5000) return;
      lastSent.current = Date.now();
      fetch(API_ENDPOINTS.activityPing, {
        method: 'POST',
        keepalive: true,
        headers: { ...getBearerAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: pageRef.current || null, device: device() }),
      }).catch(() => {});
    };
    let throttled = 0;
    const onInput = () => {
      const now = Date.now();
      if (now - throttled < 5000) return;
      throttled = now;
      lastInput.current = now;
    };
    const events = ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, onInput, { passive: true }));
    const onVisible = () => { if (document.visibilityState === 'visible') { lastInput.current = Date.now(); send(); } };
    document.addEventListener('visibilitychange', onVisible);
    send();
    const timer = window.setInterval(send, EVERY_MS);
    return () => {
      window.clearInterval(timer);
      events.forEach((e) => window.removeEventListener(e, onInput));
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [signedIn]);
}
